import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Info, Minus } from 'lucide-react';
import { Card } from '../../components/ui/Card';

interface RoleInfo {
  name: string;
  summary: string;
  accent: string; // badge colour
  can: string[];
  limited: string[];
}

// Mirrors docker/admin-api/src/rbac/permissions.ts (SECTION_ACCESS). Keep in
// sync when a section's role access changes.
const ROLES: RoleInfo[] = [
  {
    name: 'Super admin',
    accent: 'bg-primary/10 text-primary',
    summary: 'Unrestricted. The only role that can manage admin users.',
    can: [
      'Everything Operations and Support can do',
      'Invite, deactivate and set the role of admin users',
      'View the full admin activity log',
    ],
    limited: [],
  },
  {
    name: 'Operations',
    accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    summary: 'Runs the racing & content pipeline.',
    can: [
      'Meetings, Predicted Exotics, Analysis Models, Tracks & tags — full edit',
      'AI Prompts and System Settings (incl. the global paywall) — full edit',
      'Marketing — full edit',
      'Analytics and Customers — view only',
    ],
    limited: ['No access to subscriptions, packages, discounts, Stripe pricing, promotions or affiliates'],
  },
  {
    name: 'Support',
    accent: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    summary: 'Handles customers & billing.',
    can: [
      'Customers, Subscriptions, Packages, Discounts — full edit',
      'Stripe price mappings, Promotions, Affiliates & commissions — full edit',
      'Meetings, Analysis Models, Tracks, Marketing, Analytics, Financial reports — view only',
    ],
    limited: ['No access to AI Prompts or System Settings'],
  },
  {
    name: 'Analyst (read-only)',
    accent: 'bg-muted text-muted-foreground',
    summary: 'Sees everything, changes nothing.',
    can: ['Read-only across meetings, analytics, customers, billing and marketing', 'View the admin activity log'],
    limited: ['Cannot create, edit or delete anything'],
  },
];

export function RoleAccessGuide() {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-muted/40"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Info className="h-4 w-4 text-primary" />
          Roles & access — what each role can do
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-4 border-t border-border p-4 md:grid-cols-2">
          {ROLES.map((role) => (
            <div key={role.name} className="rounded-lg border border-border p-3">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${role.accent}`}>{role.name}</span>
              <p className="mt-2 text-sm text-muted-foreground">{role.summary}</p>
              <ul className="mt-2 space-y-1">
                {role.can.map((item) => (
                  <li key={item} className="flex gap-1.5 text-sm">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span>{item}</span>
                  </li>
                ))}
                {role.limited.map((item) => (
                  <li key={item} className="flex gap-1.5 text-sm text-muted-foreground">
                    <Minus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
